import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Modal } from "../../common/Modal/Modal";
import { Heading } from "../../common/Heading/Heading";
import { Dropdown } from "../../common/Dropdown/Dropdown";
import { Button } from "../../common/Button/Button";
import InputField from "../../common/InputField/InputField";
import { useTranslation } from "react-i18next";
import { useData } from "../../../context/DataContext";
import { daysToSeconds } from "../../../helpers/date-helpers";

export const CreateInvitationModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const {
    organizations,
    createInvitation,
    isLoading: contextLoading,
  } = useData();

  const [orgId, setOrgId] = useState("");
  const [role, setRole] = useState("user");
  const [expiresIn, setExpiresIn] = useState("7d");
  const [maxUses, setMaxUses] = useState("1");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);

  useEffect(() => {
    if (organizations && organizations.length > 0 && !orgId) {
      setOrgId(organizations[0].id.toString());
    }
  }, [organizations, orgId]);

  const orgOptions = organizations.reduce((acc, org) => {
    acc[org.id] = org.name;
    return acc;
  }, {});

  const roleOptions = {
    user: t("roles.user"),
    admin: t("roles.admin"),
  };

  const expiryOptions = {
    "1d": t("invitations.day1"),
    "3d": t("invitations.days3"),
    "7d": t("invitations.days7"),
    "30d": t("invitations.days30"),
  };

  const handleOrgSelect = (value) => {
    setOrgId(value);
  };

  const handleRoleSelect = (value) => {
    setRole(value);
  };

  const handleExpirySelect = (value) => {
    setExpiresIn(value);
  };

  const handleSubmit = async () => {
    setError(null);
    setIsLoading(true);
    setCreatedToken(null);

    if (!maxUses || parseInt(maxUses) < 1) {
      setError(t("common.invalidMaxUses"));
      setIsLoading(false);
      return;
    }

    if (!orgId) {
      setError(t("invitations.organization") + " " + t("common.isRequired"));
      setIsLoading(false);
      return;
    }

    const data = {
      orgid: parseInt(orgId),
      role,
      expiresIn: daysToSeconds(expiresIn),
      maxUses: parseInt(maxUses),
    };

    const result = await createInvitation(data);
    setIsLoading(false);

    if (result.success) {
      setCreatedToken(result.data.token);
    } else {
      setError(result.message);
    }
  };

  const resetForm = () => {
    setOrgId(organizations.length > 0 ? organizations[0].id.toString() : "");
    setRole("user");
    setExpiresIn("7d");
    setMaxUses("1");
    setError(null);
    setCreatedToken(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCopyToken = () => {
    if (createdToken) {
      navigator.clipboard
        .writeText(createdToken)
        .then(() => {
          // Could add a "copied" notification here
        })
        .catch((err) => {
          console.error("Failed to copy token: ", err);
        });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <FormContainer>
        <Heading
          text={t("invitations.createInvitation")}
          size={2}
          marginBottom="1.5"
        />
        {createdToken ? (
          <TokenCreatedContainer>
            <SuccessMessage>
              {t("invitations.invitationCreatedSuccess")}
            </SuccessMessage>
            <TokenDisplay>
              <TokenLabel>{t("invitations.yourToken")}:</TokenLabel>
              <TokenValue>{createdToken}</TokenValue>
            </TokenDisplay>
            <ButtonsContainer>
              <Button
                text={t("common.copy")}
                onClick={handleCopyToken}
                variant="primary"
              />
              <Button
                text={t("common.close")}
                onClick={handleClose}
                variant="white"
              />
            </ButtonsContainer>
          </TokenCreatedContainer>
        ) : (
          <Form>
            {organizations.length > 0 && (
              <DropdownsContainer>
                <Dropdown
                  label={t("invitations.organization")}
                  options={orgOptions}
                  defaultSelected={orgId}
                  handleSelect={handleOrgSelect}
                  allowFiltering={true}
                />

                <Dropdown
                  label={t("invitations.role")}
                  options={roleOptions}
                  defaultSelected={role}
                  handleSelect={handleRoleSelect}
                />

                <Dropdown
                  label={t("invitations.expiresIn")}
                  options={expiryOptions}
                  defaultSelected={expiresIn}
                  handleSelect={handleExpirySelect}
                />
              </DropdownsContainer>
            )}

            <InputField
              label={t("invitations.maxUses")}
              placeholder={t("invitations.enterMaxUses")}
              value={maxUses}
              onChange={setMaxUses}
              required
              type="number"
              min="1"
            />

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ButtonsContainer>
              <Button
                text={t("common.cancel")}
                onClick={handleClose}
                variant="white"
              />
              <Button
                disabled={isLoading || contextLoading}
                onClick={handleSubmit}
                text={
                  isLoading
                    ? t("common.creating")
                    : t("invitations.createInvitation")
                }
              />
            </ButtonsContainer>
          </Form>
        )}
      </FormContainer>
    </Modal>
  );
};

const FormContainer = styled.div``;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const DropdownsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 1rem;
  margin-top: 1rem;
`;

const TokenCreatedContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SuccessMessage = styled.div`
  color: #047857;
  background-color: #ecfdf5;
  border: 1px solid #a7f3d0;
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
`;

const TokenDisplay = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const TokenLabel = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;
`;

const TokenValue = styled.div`
  font-family: monospace;
  font-size: 1.5rem;
  padding: 0.5rem;
  text-align: center;
  background-color: white;
  border: 1px dashed #cbd5e1;
  border-radius: 0.25rem;
`;

const ErrorMessage = styled.div`
  color: #d52566;
  background-color: #fff5f7;
  border: 1px solid #ffc2d1;
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-top: 1rem;
  font-size: 0.875rem;
`;
